import {
  CaptionedImage,
  DetailsPanel,
  GenderIcon,
} from '@/components/controls';
import { useNichePersonImage } from '@/hooks';
import { toProperCase } from '@/utils';
import {
  EditDocument as EditDocumentIcon,
  Map as MapIcon,
  Psychology as PsychologyIcon,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Card,
  CardHeader,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { differenceInYears, format } from 'date-fns';

function Subject({ subject }) {
  const {
    query: { data: imageSrc, isFetching },
  } = useNichePersonImage(subject?.id);

  if (isFetching) {
    return <Skeleton variant="rounded" sx={{ minWidth: 200 }} height={240} />;
  }

  return (
    <Stack spacing={0.5} sx={{ minWidth: 200 }}>
      <CaptionedImage
        imageSrc={imageSrc}
        height={240}
        fallbackWidth={200}
        topCaption={
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Box>
              {toProperCase(
                [subject?.forenames, subject?.surname]
                  .filter(Boolean)
                  .join(' '),
              )}
            </Box>
            <GenderIcon gender={subject?.gender} />
          </Stack>
        }
        bottomCaption={
          subject?.dateOfBirth && (
            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
              <Typography>
                {format(subject.dateOfBirth, 'dd/MM/yyyy')}
              </Typography>
              <Typography sx={{ color: 'primary.main' }}>
                ({differenceInYears(new Date(), subject.dateOfBirth)})
              </Typography>
            </Stack>
          )
        }
      />
      <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
        <Tooltip
          title={
            subject?.publicProtectionNoticeCompleted
              ? 'PPN complete'
              : 'PPN outstanding'
          }
          placement="top-end"
          arrow
        >
          <EditDocumentIcon
            color={
              subject?.publicProtectionNoticeCompleted ? 'success' : 'error'
            }
            sx={{ fontSize: 16 }}
          />
        </Tooltip>
      </Stack>
    </Stack>
  );
}

export function MentalHealthIncident({ incident, onShowMapClick }) {
  const { occurrence, subjects = [] } = incident;

  return (
    <Card variant="outlined">
      <CardHeader
        avatar={
          <Avatar>
            <PsychologyIcon />
          </Avatar>
        }
        action={
          occurrence?.address && (
            <Tooltip title="Show map">
              <IconButton size="small" onClick={onShowMapClick}>
                <MapIcon sx={{ fontSize: 'inherit' }} />
              </IconButton>
            </Tooltip>
          )
        }
        title={occurrence?.number}
        subheader={
          occurrence?.reportedTime &&
          format(occurrence.reportedTime, 'dd/MM/yyyy HH:mm')
        }
      />
      {subjects.length > 0 && (
        <Stack
          direction="row"
          spacing={1}
          sx={{ overflowX: 'auto', px: 1, pb: 1 }}
        >
          {subjects.map((subject) => (
            <Subject key={subject.id} subject={subject} />
          ))}
        </Stack>
      )}
      <Stack spacing={1} sx={{ p: 1 }}>
        {occurrence?.summary && (
          <Typography variant="body2" sx={{ p: 0.5 }}>
            {occurrence.summary}
          </Typography>
        )}
        <DetailsPanel
          title="Owned by"
          entries={[
            {
              label: 'OIC',
              value: occurrence?.officerInCase?.collarNumber,
            },
            {
              label: 'Team',
              value: occurrence?.officerInCase?.team,
            },
          ]}
        />
      </Stack>
    </Card>
  );
}
